"use client";

import React, { useState } from "react";
import { Brain, Globe, Users, Network, Database, Cpu, Zap, Activity } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function IntelligenceLayer() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const businessName = "MollyCall.ai";
  const features = [{
    id: "website-scan",
    title: "Website Intelligence Scan",
    icon: <Globe className="size-6" />,
    description: "Automatically crawls and learns from your company website content to build a knowledge base instantly.",
    position: "top-left",
    json: null
  }, {
    id: "profile-sync",
    title: "ProfileSync",
    icon: <Database className="size-6" />,
    description: "Real-time customer personalization using CRM data to tailor every conversation.",
    position: "top-right",
    json: {
      customer_id: "8492-A",
      sentiment: "high_intent",
      last_interaction: "2m ago",
      crm_status: "qualified_lead",
      next_action: "schedule_demo"
    }
  }, {
    id: "multi-agent",
    title: "Multi-Agent Enhancement",
    icon: <Users className="size-6" />,
    description: "Analyzes millions of AI and human interactions to continuously refine conversation flows.",
    position: "bottom-left",
    json: null
  }, {
    id: "community-intel",
    title: "Community Intelligence",
    icon: <Network className="size-6" />,
    description: "Leverages industry-wide insights to optimize performance benchmarks and strategies.",
    position: "bottom-right",
    json: null
  }];
  return <section id="intelligence-layer" ref={ref} className={`relative overflow-hidden bg-foreground py-24 text-background transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-pattern)" />
        </svg>
      </div>

      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,var(--color-foreground)_100%)] pointer-events-none opacity-80" />

      <div className="relative z-10 container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-16 md:mb-24">
          <h2 className="text-primary font-mono text-sm tracking-widest uppercase mb-4">
            System Architecture
          </h2>
          <h3 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
            The Intelligence Layer
          </h3>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            At the core of {businessName} lies a self-improving Learning Engine that connects 
            your data, your customers, and our global intelligence network.
          </p>
        </div>

        {}
        <div className="relative w-full max-w-4xl mx-auto aspect-[4/5] md:aspect-[16/9] lg:aspect-[2/1] flex flex-col md:block items-center justify-center">
          
          {}
          <div className="hidden md:block absolute inset-0 pointer-events-none">
            <svg className="w-full h-full" viewBox="0 0 800 400" preserveAspectRatio="xMidYMid meet">
              {}
              <line x1="400" y1="200" x2="150" y2="80" stroke="var(--color-primary)" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="5,5" />
              <line x1="400" y1="200" x2="650" y2="80" stroke="var(--color-primary)" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="5,5" />
              <line x1="400" y1="200" x2="150" y2="320" stroke="var(--color-primary)" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="5,5" />
              <line x1="400" y1="200" x2="650" y2="320" stroke="var(--color-primary)" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="5,5" />
              
              {}
              <circle r="3" fill="var(--color-primary)">
                <animateMotion dur="3s" repeatCount="indefinite" path="M400,200 L150,80" />
              </circle>
              <circle r="3" fill="var(--color-primary)">
                <animateMotion dur="3s" repeatCount="indefinite" begin="0.5s" path="M400,200 L650,80" />
              </circle>
              <circle r="3" fill="var(--color-primary)">
                <animateMotion dur="3s" repeatCount="indefinite" begin="1s" path="M400,200 L150,320" />
              </circle>
              <circle r="3" fill="var(--color-primary)">
                <animateMotion dur="3s" repeatCount="indefinite" begin="1.5s" path="M400,200 L650,320" />
              </circle>
            </svg>
          </div>

          {}
          <div className="md:absolute md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-20 mb-12 md:mb-0">
            <div className="relative group">
              <div className="absolute -inset-4 bg-primary/20 rounded-full blur-xl animate-pulse" />
              <div className="relative flex flex-col items-center justify-center size-32 md:size-40 rounded-full bg-background border-2 border-primary shadow-[0_0_30px_rgba(59,130,246,0.3)] z-20">
                <Brain className="size-12 md:size-16 text-primary mb-2" />
                <span className="text-xs font-bold text-foreground uppercase tracking-wider">Core</span>
              </div>
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 text-center w-48">
                <h4 className="text-white font-bold text-lg">Learning Engine</h4>
                <p className="text-xs text-gray-400 mt-1">Continuous self-improvement loop</p>
              </div>
            </div>
          </div>

          {}
          <div className="grid grid-cols-1 gap-6 md:block w-full">
            {features.map((feature, i) => {
            const positionClasses = {
              "top-left": "md:absolute md:top-[10%] md:left-[5%] lg:left-[10%]",
              "top-right": "md:absolute md:top-[10%] md:right-[5%] lg:right-[10%]",
              "bottom-left": "md:absolute md:bottom-[10%] md:left-[5%] lg:left-[10%]",
              "bottom-right": "md:absolute md:bottom-[10%] md:right-[5%] lg:right-[10%]"
            };
            return <div key={feature.id} data-index={i} className={`w-full md:w-72 ${positionClasses[feature.position as keyof typeof positionClasses]}`}>
                  <HoverCard openDelay={0} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <div className="group relative bg-card/5 border border-white/10 hover:border-primary/50 hover:bg-card/10 p-5 rounded-xl transition-all duration-300 cursor-pointer backdrop-blur-sm">
                        <div className="flex items-start gap-4">
                          <div className="p-3 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                            {feature.icon}
                          </div>
                          <div>
                            <h4 className="text-white font-semibold text-lg group-hover:text-primary transition-colors">
                              {feature.title}
                            </h4>
                            <p className="text-sm text-gray-400 mt-1 line-clamp-2">
                              {feature.description}
                            </p>
                          </div>
                        </div>
                        
                        {}
                        <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-white/20 group-hover:border-primary transition-colors rounded-tl-sm" />
                        <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-white/20 group-hover:border-primary transition-colors rounded-br-sm" />
                      </div>
                    </HoverCardTrigger>
                    
                    <HoverCardContent side="top" align="center" className="w-80 bg-background/95 border-primary/20 backdrop-blur-xl text-foreground p-0 overflow-hidden shadow-2xl">
                      <div className="p-4 border-b border-border/50 bg-muted/30">
                        <div className="flex items-center gap-2 mb-2">
                          {feature.icon}
                          <h5 className="font-semibold text-sm">{feature.title}</h5>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {feature.description}
                        </p>
                      </div>
                      
                      {feature.json ? <div className="p-4 bg-black/90 font-mono text-xs overflow-x-auto">
                          <div className="flex items-center justify-between mb-2 text-gray-500 border-b border-gray-800 pb-1">
                            <span>JSON Preview</span>
                            <span className="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded">LIVE</span>
                          </div>
                          <pre className="text-green-400">
                            {`{`}
                            {Object.entries(feature.json).map(([key, value], idx, arr) => <div key={key} className="pl-4">
                                <span className="text-blue-400">"{key}"</span>: <span className="text-orange-300">"{value}"</span>
                                {idx < arr.length - 1 && ","}
                              </div>)}
                            {`}`}
                          </pre>
                        </div> : <div className="p-4 bg-muted/10">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                            <Activity className="size-3" />
                            <span>System Status: Active</span>
                          </div>
                          <div className="h-1 w-full bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary w-2/3 animate-[pulse_2s_ease-in-out_infinite]" />
                          </div>
                        </div>}
                    </HoverCardContent>
                  </HoverCard>
                </div>;
          })}
          </div>
        </div>

        <div className="mt-16 md:mt-24 text-center">
          <Link to="/request-demo">
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg rounded-full shadow-[0_0_20px_rgba(59,130,246,0.4)] hover:shadow-[0_0_30px_rgba(59,130,246,0.6)] transition-all">
              <Zap className="mr-2 size-5 fill-current" />
              Deploy Intelligence Layer
            </Button>
          </Link>
          <p className="mt-4 text-sm text-gray-500">
            Seamless integration with your existing tech stack.
          </p>
        </div>
      </div>
    </section>;
}